import { useState } from 'react';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { deleteDataCatalog, IDataCatalog, updateOrganization, VerdocsEndpoint } from '@verdocs/js-sdk';
import { ChevronDownIcon, CodeBracketIcon, EllipsisVerticalIcon, PencilIcon, Squares2X2Icon } from '@heroicons/react/24/outline';
import { Badge, EmptyState, FIELD_CLASSES, FlexFill, HelpDialog, PageTitle, SegmentedControl, Spinner } from '../../Components';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../Components/DropdownMenu';
import { DataCatalogMarketplaceDialog } from '../../Dialogs/DataCatalogMarketplaceDialog';
import { invalidateDataCatalogs, useDataCatalogs } from '../../react-sdk/queries';
import { CreateDataCatalogDialog } from '../../Dialogs/CreateDataCatalogDialog';
import { ImportDataCatalogDialog } from '../../Dialogs/ImportDataCatalogDialog';
import { OkCancelDialog } from '../../Dialogs/OkCancelDialog';
import { AppState } from '../../AppState';
import { URLS } from '../../Routing/urls';

type TCreatePath = 'scratch' | 'import' | 'marketplace';

const CREATE_PATHS: { key: TCreatePath; icon: typeof PencilIcon; title: string; hint: string }[] = [
  { key: 'scratch', icon: PencilIcon, title: 'From scratch', hint: 'Name it and add keys yourself' },
  { key: 'import', icon: CodeBracketIcon, title: 'Import keys', hint: 'Sample JSON, a JSON Schema, a CSV header row, or a shared catalog' },
  { key: 'marketplace', icon: Squares2X2Icon, title: 'From the marketplace', hint: 'Catalogs shared with you' },
];

const SOURCES = { scratch: 'From scratch', import: 'Imported', marketplace: 'From the marketplace' };

const needsAttention = (catalog: IDataCatalog) => !!catalog.update_available || !!catalog.has_unreleased_changes;

export function DataCatalogs() {
  const navigate = useNavigate();
  const organizationId = AppState.currentProfile?.organization_id || '';
  const catalogsQuery = useDataCatalogs(organizationId);
  const [ filter, setFilter ] = useState('all');
  const [ search, setSearch ] = useState('');
  const [ creating, setCreating ] = useState<TCreatePath | null>(null);
  const [ deleting, setDeleting ] = useState<IDataCatalog | null>(null);
  const [ working, setWorking ] = useState(false);

  const catalogs = catalogsQuery.data || [];
  const query = search.trim().toLowerCase();
  const shown = catalogs
    .filter(catalog => filter === 'all' || (filter === 'attention' ? needsAttention(catalog) : catalog.sharing === 'shared'))
    .filter(catalog => !query || `${catalog.name} ${catalog.description || ''}`.toLowerCase().includes(query));

  const filters = [
    { key: 'all', label: `All ${catalogs.length}` },
    { key: 'attention', label: `Needs attention ${catalogs.filter(needsAttention).length}` },
    { key: 'shared', label: `Shared ${catalogs.filter(catalog => catalog.sharing === 'shared').length}` },
  ];

  const openCatalog = (catalogId: string) => {
    setCreating(null);
    invalidateDataCatalogs();
    return navigate(URLS.SETTINGS_DATA_CATALOG.build(catalogId));
  };

  const handleCreated = (catalog: IDataCatalog) => openCatalog(catalog.id);

  const handleSetDefault = async (catalog: IDataCatalog) => {
    setWorking(true);
    try {
      await updateOrganization(VerdocsEndpoint.getDefault(), organizationId, { default_data_catalog_id: catalog.id });
      invalidateDataCatalogs();
      toast.success(`${catalog.name} is now the default catalog`);
    } catch (e: any) {
      toast.error(`Error setting the default catalog: ${e.message}`);
    }
    setWorking(false);
  };

  const handleDelete = async () => {
    if (!deleting) return;
    setWorking(true);
    try {
      await deleteDataCatalog(VerdocsEndpoint.getDefault(), organizationId, deleting.id);
      invalidateDataCatalogs();
      toast.success(`${deleting.name} deleted`);
    } catch (e: any) {
      const templates: { name: string }[] = e.response?.data?.templates || [];
      const detail = templates.length ? ` Templates using it: ${templates.map(template => template.name).join(', ')}.` : '';
      toast.error(`${e.response?.data?.message || e.message}.${detail}`);
    }
    setDeleting(null);
    setWorking(false);
  };

  const createMenu = (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="inline-flex h-8 items-center gap-1.5 rounded-md bg-[#2e9b64] px-3.5 text-[13px] font-medium text-white hover:bg-[#278a58]">
          New catalog
          <ChevronDownIcon className="size-3.5" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-[260px] bg-white p-1">
        {CREATE_PATHS.map(({ key, icon: Icon, title, hint }) => (
          <DropdownMenuItem
            key={key}
            className="cursor-pointer items-start gap-2.5 py-2"
            onClick={() => setCreating(key)}>
            <span className="flex size-7 shrink-0 items-center justify-center rounded-md bg-indigo-50 text-indigo-600">
              <Icon className="size-4" />
            </span>
            <span>
              <span className="block text-[13px] font-medium text-[#33364b]">{title}</span>
              <span className="block text-[12px] text-gray-500">{hint}</span>
            </span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return (
    <div className="flex flex-1 flex-col box-border overflow-y-scroll">
      <PageTitle>
        Data Catalogs
        <FlexFill />
        <div className="flex items-center gap-3">
          <HelpDialog title="Data Catalogs">
            <p className="mb-4">
              A data catalog lists the keys your template fields can be filled from, such as <code>applicant.full_name</code>. Template
              owners pick keys from it, and developers use it to know what to send in <code>prefill_data</code>.
            </p>
            <p>
              Your default catalog applies to every template that doesn&apos;t choose its own. Catalogs shared with your organization appear
              in the marketplace, and importing one gives you a copy you can change.
            </p>
          </HelpDialog>
          {catalogs.length > 0 && createMenu}
        </div>
      </PageTitle>

      <div className="w-full max-w-[1100px]">
        <p className="mb-5 max-w-[600px] text-sm leading-relaxed text-gray-500">
          A data catalog lists the keys your template fields can be filled from.
        </p>

        {catalogsQuery.isLoading ? (
          <div className="flex items-center justify-center py-20">
            <Spinner mode="dark" />
          </div>
        ) : catalogs.length === 0 ? (
          <EmptyState
            title="No data catalogs yet"
            action={createMenu}>
            Start one from scratch, import a JSON sample or schema, or copy one from the marketplace.
          </EmptyState>
        ) : (
          <>
            <div className="mb-3 flex flex-wrap items-center gap-3">
              <div className="w-[260px]">
                <input
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="Filter catalogs"
                  className={FIELD_CLASSES}
                />
              </div>
              <SegmentedControl
                options={filters}
                value={filter}
                onChange={setFilter}
              />
              {working && <Spinner mode="dark" />}
            </div>

            <div className="overflow-hidden rounded-lg border border-[#ebecf0]">
              <table className="w-full text-[13px]">
                <thead className="border-b border-[#ebecf0] bg-[#fafafb]">
                  <tr className="text-left text-[12px] text-gray-500">
                    <th className="px-3 py-2 font-medium">Name</th>
                    <th className="px-3 py-2 font-medium">Source</th>
                    <th className="px-3 py-2 text-right font-medium">Keys</th>
                    <th className="px-3 py-2 font-medium">Shared with</th>
                    <th className="px-3 py-2 font-medium">Status</th>
                    <th className="w-10" />
                  </tr>
                </thead>
                <tbody>
                  {shown.map(catalog => (
                    <tr
                      key={catalog.id}
                      onClick={() => navigate(URLS.SETTINGS_DATA_CATALOG.build(catalog.id))}
                      className="cursor-pointer border-t border-[#f2f3f5] first:border-0 hover:bg-[#fafaff]">
                      <td className="px-3 py-2.5">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-[#33364b]">{catalog.name}</span>
                          {catalog.is_default && (
                            <span className="rounded-full bg-indigo-50 px-2 py-0.5 text-[9.5px] font-bold uppercase tracking-wider text-indigo-600">
                              Default
                            </span>
                          )}
                        </div>
                        {catalog.description && (
                          <div className="mt-0.5 max-w-[420px] truncate text-[12px] text-gray-400">{catalog.description}</div>
                        )}
                      </td>
                      <td className="px-3 py-2.5 text-gray-500">{SOURCES[catalog.source]}</td>
                      <td className="px-3 py-2.5 text-right tabular-nums">{catalog.entry_count ?? 0}</td>
                      <td className="px-3 py-2.5 text-gray-500">{catalog.sharing === 'shared' ? 'Sub-accounts' : 'Private'}</td>
                      <td className="px-3 py-2.5">
                        <div className="flex flex-wrap gap-1.5">
                          {catalog.update_available && <Badge tone="warn">Update available</Badge>}
                          {catalog.has_unreleased_changes && <Badge tone="warn">Unreleased changes</Badge>}
                        </div>
                      </td>
                      <td
                        className="px-2 py-2.5"
                        onClick={e => e.stopPropagation()}>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button
                              className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                              aria-label="Catalog actions">
                              <EllipsisVerticalIcon className="size-5" />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent
                            align="end"
                            className="bg-white">
                            <DropdownMenuItem
                              className="cursor-pointer"
                              disabled={catalog.is_default}
                              onClick={() => handleSetDefault(catalog)}>
                              Set as default
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              className="cursor-pointer text-red-600 focus:text-red-600"
                              onClick={() => setDeleting(catalog)}>
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {shown.length === 0 && <div className="px-3 py-6 text-center text-[13px] text-gray-500">No catalogs match.</div>}
            </div>
          </>
        )}
      </div>

      {creating === 'scratch' && (
        <CreateDataCatalogDialog
          onClose={() => setCreating(null)}
          onCreated={handleCreated}
        />
      )}
      {creating === 'import' && (
        <ImportDataCatalogDialog
          onClose={() => setCreating(null)}
          onDone={openCatalog}
        />
      )}
      {creating === 'marketplace' && (
        <DataCatalogMarketplaceDialog
          onClose={() => setCreating(null)}
          onImported={handleCreated}
        />
      )}

      {deleting && (
        <OkCancelDialog
          onOk={handleDelete}
          onCancel={() => setDeleting(null)}>
          <p className="mb-6">
            Delete the catalog <strong>{deleting.name}</strong>?{' '}
            {deleting.is_default && 'It\'s your default, so new templates won\'t have a catalog until you pick a new default. '}
            This can&apos;t be undone.
          </p>
        </OkCancelDialog>
      )}
    </div>
  );
}
